// ─── Types ────────────────────────────────────────────────────────────────────

export type TestCategory =
  | "Blood Test"
  | "Urine Test"
  | "Thyroid"
  | "Diabetes"
  | "Heart Health"
  | "Liver"
  | "Kidney"
  | "Vitamins"
  | "Allergy"
  | "Cancer Marker";

export interface LabTest {
  id: string;
  name: string;
  category: TestCategory;
  price: number;
  discountedPrice: number;
  turnaround: string;
  description: string;
  parameters: string[];
  popular: boolean;
  fasting: boolean;
  homeCollection: boolean;
}

export interface TestPackage {
  id: string;
  name: string;
  tagline: string;
  tests: string[];
  testCount: number;
  price: number;
  discountedPrice: number;
  popular: boolean;
  color: string;
  turnaround: string;
  fasting: boolean;
  homeCollection: boolean;
}

export type BookingStatus = "Pending" | "Confirmed" | "Sample Collected" | "Report Ready" | "Cancelled";
export type CollectionType = "Home Collection" | "Walk-in";
export type PaymentStatus = "Pending" | "Paid" | "Refunded";
export type PaymentMethod = "UPI" | "Credit Card" | "Debit Card" | "Net Banking";

export interface Booking {
  id: string;
  patientId: string;
  patientName: string;
  patientPhone: string;
  patientEmail: string;
  testIds: string[];
  packageIds: string[];
  testNames: string[];
  collectionType: CollectionType;
  address?: string;
  city?: string;
  pincode?: string;
  slotDate: string;
  slotTime: string;
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  paymentMethod?: PaymentMethod;
  totalAmount: number;
  paidAmount?: number;
  createdAt: string;
  notes?: string;
}

export interface Patient {
  id: string;
  name: string;
  email: string;
  phone: string;
  dob: string;
  gender: "Male" | "Female" | "Other";
  address: string;
  city: string;
  pincode: string;
  bookingIds: string[];
  createdAt: string;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: "Admin" | "Lab Technician" | "Receptionist";
}

// ─── Seed Data ────────────────────────────────────────────────────────────────

export const labTests: LabTest[] = [
  { id: "t1", name: "Complete Blood Count (CBC)", category: "Blood Test", price: 599, discountedPrice: 299, turnaround: "4-6 hours", description: "Measures different components of blood including RBC, WBC, platelets.", parameters: ["Hemoglobin", "RBC Count", "WBC Count", "Platelet Count", "Hematocrit", "MCV", "MCH", "MCHC"], popular: true, fasting: false, homeCollection: true },
  { id: "t2", name: "Lipid Profile", category: "Heart Health", price: 899, discountedPrice: 449, turnaround: "6-8 hours", description: "Measures cholesterol and triglycerides to assess heart disease risk.", parameters: ["Total Cholesterol", "HDL", "LDL", "VLDL", "Triglycerides", "Cholesterol/HDL Ratio"], popular: true, fasting: true, homeCollection: true },
  { id: "t3", name: "Thyroid Profile (T3, T4, TSH)", category: "Thyroid", price: 799, discountedPrice: 399, turnaround: "Same Day", description: "Evaluates thyroid gland function.", parameters: ["T3", "T4", "TSH"], popular: true, fasting: false, homeCollection: true },
  { id: "t4", name: "HbA1c (Glycated Hemoglobin)", category: "Diabetes", price: 599, discountedPrice: 349, turnaround: "Same Day", description: "Average blood sugar levels over last 3 months.", parameters: ["HbA1c %", "Average Blood Glucose"], popular: true, fasting: false, homeCollection: true },
  { id: "t5", name: "Fasting Blood Sugar", category: "Diabetes", price: 299, discountedPrice: 149, turnaround: "2-4 hours", description: "Measures glucose level after 8-12 hours of fasting.", parameters: ["Glucose - Fasting"], popular: false, fasting: true, homeCollection: true },
  { id: "t6", name: "Liver Function Test (LFT)", category: "Liver", price: 899, discountedPrice: 499, turnaround: "Same Day", description: "Assesses liver health and function.", parameters: ["SGOT", "SGPT", "ALP", "Bilirubin Total", "Direct Bilirubin", "Indirect Bilirubin", "Total Protein", "Albumin"], popular: true, fasting: true, homeCollection: true },
  { id: "t7", name: "Kidney Function Test (KFT)", category: "Kidney", price: 799, discountedPrice: 449, turnaround: "Same Day", description: "Evaluates kidney function and health.", parameters: ["Creatinine", "BUN", "Uric Acid", "Sodium", "Potassium", "Chloride"], popular: false, fasting: true, homeCollection: true },
  { id: "t8", name: "Vitamin D Total (25-OH)", category: "Vitamins", price: 1299, discountedPrice: 699, turnaround: "24 hours", description: "Measures Vitamin D levels in blood.", parameters: ["25-OH Vitamin D"], popular: true, fasting: false, homeCollection: true },
  { id: "t9", name: "Vitamin B12", category: "Vitamins", price: 999, discountedPrice: 549, turnaround: "24 hours", description: "Measures Vitamin B12 levels in blood.", parameters: ["Vitamin B12"], popular: false, fasting: false, homeCollection: true },
  { id: "t10", name: "Urine Routine Examination", category: "Urine Test", price: 299, discountedPrice: 149, turnaround: "2-4 hours", description: "General urine analysis for infection, kidney issues etc.", parameters: ["Color", "Clarity", "pH", "Protein", "Glucose", "Ketones", "RBC", "WBC", "Casts"], popular: false, fasting: false, homeCollection: true },
  { id: "t11", name: "PSA (Prostate Specific Antigen)", category: "Cancer Marker", price: 1299, discountedPrice: 749, turnaround: "24 hours", description: "Screening test for prostate health.", parameters: ["Total PSA", "Free PSA"], popular: false, fasting: false, homeCollection: true },
  { id: "t12", name: "Iron Studies", category: "Blood Test", price: 999, discountedPrice: 549, turnaround: "Same Day", description: "Assesses iron levels and storage in the body.", parameters: ["Serum Iron", "TIBC", "Transferrin Saturation", "Ferritin"], popular: false, fasting: true, homeCollection: true },
];

export const testPackages: TestPackage[] = [
  { id: "p1", name: "Aarogya Basic Health Checkup", tagline: "Your first step to a healthier you", tests: ["t1", "t3", "t4", "t5", "t10"], testCount: 38, price: 1999, discountedPrice: 799, popular: true, color: "blue", turnaround: "Same Day", fasting: true, homeCollection: true },
  { id: "p2", name: "Aarogya Advanced Full Body", tagline: "Comprehensive wellness screening", tests: ["t1", "t2", "t3", "t4", "t6", "t7", "t8", "t9", "t10"], testCount: 72, price: 4999, discountedPrice: 1999, popular: true, color: "purple", turnaround: "24 hours", fasting: true, homeCollection: true },
  { id: "p3", name: "Cardiac Risk Profile", tagline: "Know your heart health", tests: ["t1", "t2", "t6"], testCount: 24, price: 2499, discountedPrice: 1199, popular: false, color: "red", turnaround: "Same Day", fasting: true, homeCollection: true },
  { id: "p4", name: "Diabetes Care Package", tagline: "Monitor and manage diabetes", tests: ["t4", "t5", "t7", "t10"], testCount: 18, price: 1499, discountedPrice: 699, popular: true, color: "orange", turnaround: "Same Day", fasting: true, homeCollection: true },
  { id: "p5", name: "Women's Wellness Panel", tagline: "Designed for women's health needs", tests: ["t1", "t3", "t4", "t8", "t9", "t10"], testCount: 45, price: 3499, discountedPrice: 1499, popular: true, color: "pink", turnaround: "24 hours", fasting: true, homeCollection: true },
  { id: "p6", name: "Senior Citizen Package", tagline: "Complete health assessment for 60+", tests: ["t1", "t2", "t3", "t4", "t6", "t7", "t8", "t9", "t11"], testCount: 58, price: 5999, discountedPrice: 2499, popular: false, color: "green", turnaround: "24 hours", fasting: true, homeCollection: true },
];

export const bookings: Booking[] = [
  { id: "b1", patientId: "u1", patientName: "Arjun Sharma", patientPhone: "9876543210", patientEmail: "arjun@example.com", testIds: ["t1", "t3"], packageIds: [], testNames: ["Complete Blood Count (CBC)", "Thyroid Profile"], collectionType: "Home Collection", address: "45, MG Road", city: "Bengaluru", pincode: "560001", slotDate: "2025-07-10", slotTime: "08:00 AM", status: "Confirmed", paymentStatus: "Paid", paymentMethod: "UPI", totalAmount: 698, paidAmount: 698, createdAt: "2025-07-08T10:30:00Z" },
  { id: "b2", patientId: "u2", patientName: "Priya Patel", patientPhone: "9123456789", patientEmail: "priya@example.com", testIds: [], packageIds: ["p2"], testNames: ["Aarogya Advanced Full Body"], collectionType: "Walk-in", slotDate: "2025-07-11", slotTime: "10:30 AM", status: "Pending", paymentStatus: "Pending", totalAmount: 1999, createdAt: "2025-07-08T14:00:00Z" },
  { id: "b3", patientId: "u3", patientName: "Rahul Mehta", patientPhone: "9988776655", patientEmail: "rahul@example.com", testIds: ["t2", "t6"], packageIds: [], testNames: ["Lipid Profile", "Liver Function Test (LFT)"], collectionType: "Home Collection", address: "12, Park Street", city: "Mumbai", pincode: "400001", slotDate: "2025-07-09", slotTime: "07:00 AM", status: "Sample Collected", paymentStatus: "Paid", paymentMethod: "Credit Card", totalAmount: 948, paidAmount: 948, createdAt: "2025-07-07T09:00:00Z" },
  { id: "b4", patientId: "u4", patientName: "Sunita Rao", patientPhone: "9765432109", patientEmail: "sunita@example.com", testIds: [], packageIds: ["p5"], testNames: ["Women's Wellness Panel"], collectionType: "Home Collection", address: "78, Anna Nagar", city: "Chennai", pincode: "600040", slotDate: "2025-07-09", slotTime: "09:00 AM", status: "Report Ready", paymentStatus: "Paid", paymentMethod: "Debit Card", totalAmount: 1499, paidAmount: 1499, createdAt: "2025-07-06T11:00:00Z" },
  { id: "b5", patientId: "u5", patientName: "Vikram Singh", patientPhone: "9654321098", patientEmail: "vikram@example.com", testIds: ["t4", "t5"], packageIds: [], testNames: ["HbA1c", "Fasting Blood Sugar"], collectionType: "Walk-in", slotDate: "2025-07-12", slotTime: "11:00 AM", status: "Pending", paymentStatus: "Pending", totalAmount: 498, createdAt: "2025-07-08T16:30:00Z" },
  { id: "b6", patientId: "u6", patientName: "Kavitha Nair", patientPhone: "9543210987", patientEmail: "kavitha@example.com", testIds: ["t8", "t9"], packageIds: [], testNames: ["Vitamin D Total", "Vitamin B12"], collectionType: "Home Collection", address: "23, Koregaon Park", city: "Pune", pincode: "411001", slotDate: "2025-07-10", slotTime: "08:30 AM", status: "Confirmed", paymentStatus: "Paid", paymentMethod: "Net Banking", totalAmount: 1248, paidAmount: 1248, createdAt: "2025-07-08T08:00:00Z" },
];

export const patients: Patient[] = [
  { id: "u1", name: "Arjun Sharma", email: "arjun@example.com", phone: "9876543210", dob: "1990-05-15", gender: "Male", address: "45, MG Road", city: "Bengaluru", pincode: "560001", bookingIds: ["b1"], createdAt: "2025-01-01T00:00:00Z" },
  { id: "u2", name: "Priya Patel", email: "priya@example.com", phone: "9123456789", dob: "1985-08-22", gender: "Female", address: "10, Residency Road", city: "Bengaluru", pincode: "560025", bookingIds: ["b2"], createdAt: "2025-02-15T00:00:00Z" },
  { id: "u3", name: "Rahul Mehta", email: "rahul@example.com", phone: "9988776655", dob: "1978-11-03", gender: "Male", address: "12, Park Street", city: "Mumbai", pincode: "400001", bookingIds: ["b3"], createdAt: "2025-03-10T00:00:00Z" },
  { id: "u4", name: "Sunita Rao", email: "sunita@example.com", phone: "9765432109", dob: "1965-02-14", gender: "Female", address: "78, Anna Nagar", city: "Chennai", pincode: "600040", bookingIds: ["b4"], createdAt: "2025-01-20T00:00:00Z" },
  { id: "u5", name: "Vikram Singh", email: "vikram@example.com", phone: "9654321098", dob: "1982-07-30", gender: "Male", address: "55, Civil Lines", city: "Delhi", pincode: "110001", bookingIds: ["b5"], createdAt: "2025-04-05T00:00:00Z" },
  { id: "u6", name: "Kavitha Nair", email: "kavitha@example.com", phone: "9543210987", dob: "1995-12-01", gender: "Female", address: "23, Koregaon Park", city: "Pune", pincode: "411001", bookingIds: ["b6"], createdAt: "2025-05-12T00:00:00Z" },
];

// ─── In-memory store ──────────────────────────────────────────────────────────

let bookingsStore: Booking[] = [...bookings];
let patientsStore: Patient[] = [...patients];

// ─── Service Functions ────────────────────────────────────────────────────────

export const getLabTests = async (): Promise<LabTest[]> => {
  await new Promise(r => setTimeout(r, 300));
  return labTests;
};

export const getTestPackages = async (): Promise<TestPackage[]> => {
  await new Promise(r => setTimeout(r, 300));
  return testPackages;
};

export const getBookings = async (): Promise<Booking[]> => {
  await new Promise(r => setTimeout(r, 400));
  return [...bookingsStore];
};

export const getPatients = async (): Promise<Patient[]> => {
  await new Promise(r => setTimeout(r, 300));
  return [...patientsStore];
};

export const getBookingById = async (id: string): Promise<Booking | undefined> => {
  await new Promise(r => setTimeout(r, 200));
  return bookingsStore.find(b => b.id === id);
};

export const getPatientById = async (id: string): Promise<Patient | undefined> => {
  await new Promise(r => setTimeout(r, 200));
  return patientsStore.find(p => p.id === id);
};

export const getPatientByEmail = async (email: string): Promise<Patient | undefined> => {
  await new Promise(r => setTimeout(r, 200));
  return patientsStore.find(p => p.email === email);
};

export const createBooking = async (booking: Omit<Booking, "id" | "createdAt">): Promise<Booking> => {
  await new Promise(r => setTimeout(r, 500));
  const newBooking: Booking = {
    ...booking,
    id: `b${Date.now()}`,
    createdAt: new Date().toISOString(),
  };
  bookingsStore = [newBooking, ...bookingsStore];
  console.log("New booking created:", newBooking);
  return newBooking;
};

export const updateBookingStatus = async (id: string, status: BookingStatus): Promise<Booking> => {
  await new Promise(r => setTimeout(r, 300));
  bookingsStore = bookingsStore.map(b => b.id === id ? { ...b, status } : b);
  const updated = bookingsStore.find(b => b.id === id)!;
  console.log("Booking status updated:", updated);
  return updated;
};

export const updatePaymentStatus = async (id: string, paymentStatus: PaymentStatus, paymentMethod?: PaymentMethod): Promise<Booking> => {
  await new Promise(r => setTimeout(r, 300));
  bookingsStore = bookingsStore.map(b =>
    b.id === id ? { ...b, paymentStatus, paymentMethod, paidAmount: b.totalAmount } : b
  );
  const updated = bookingsStore.find(b => b.id === id)!;
  return updated;
};

export const createPatient = async (patient: Omit<Patient, "id" | "bookingIds" | "createdAt">): Promise<Patient> => {
  await new Promise(r => setTimeout(r, 400));
  const newPatient: Patient = {
    ...patient,
    id: `u${Date.now()}`,
    bookingIds: [],
    createdAt: new Date().toISOString(),
  };
  patientsStore = [...patientsStore, newPatient];
  console.log("New patient created:", newPatient);
  return newPatient;
};

export const updatePatient = async (id: string, updates: Partial<Patient>): Promise<Patient> => {
  await new Promise(r => setTimeout(r, 300));
  patientsStore = patientsStore.map(p => p.id === id ? { ...p, ...updates } : p);
  return patientsStore.find(p => p.id === id)!;
};

export const cancelBooking = async (id: string): Promise<Booking> => {
  return updateBookingStatus(id, "Cancelled");
};

export const getDashboardStats = async () => {
  await new Promise(r => setTimeout(r, 400));
  const today = new Date().toISOString().split("T")[0];
  return {
    totalBookings: bookingsStore.length,
    todayBookings: bookingsStore.filter(b => b.slotDate === today).length,
    pendingBookings: bookingsStore.filter(b => b.status === "Pending").length,
    confirmedBookings: bookingsStore.filter(b => b.status === "Confirmed").length,
    totalRevenue: bookingsStore.filter(b => b.paymentStatus === "Paid").reduce((sum, b) => sum + (b.paidAmount || 0), 0),
    homeCollections: bookingsStore.filter(b => b.collectionType === "Home Collection").length,
    reportReady: bookingsStore.filter(b => b.status === "Report Ready").length,
  };
};
